import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Archive, BadgeCheck, Loader2, CalendarClock } from "lucide-react";
import { fmtDate, toInputDate } from "@/lib/format";
import { PaymentEditor, Rep } from "@/components/ImpicDados";

const REASONS = [["proprietario", "O proprietário desistiu de vender"], ["outra_agencia", "Vendido por outra agência ou pelo próprio"], ["cmi_terminou", "O CMI terminou e não foi renovado"],
  ["comprador", "Desistência do comprador"], ["credito", "Crédito recusado"], ["avaliacao", "Avaliação bancária abaixo do preço"], ["documental", "Problema documental"], ["outro", "Outro motivo…"]];
const ANG_REASONS = new Set(["proprietario", "outra_agencia", "cmi_terminou", "outro"]);
const todayIso = () => new Date().toISOString().slice(0, 10);
const ddmm = (iso) => { const [, m, d] = String(iso).slice(0, 10).split("-"); return `${d}/${m}`; };

export const ClosedBanner = ({ p }) => {
  if (p.status !== "sem_venda") return null;
  return (
    <div className="rounded-xl px-4 py-3 text-sm border bg-slate-100 border-slate-200 text-slate-700" data-testid="closed-banner">
      <b>Sem venda · arquivado</b> {`em ${fmtDate(p.closed_at)}`}{p.closed_reason ? ` · ${p.closed_reason}` : ""}
    </div>
  );
};

const markDone = async (pid, deedDate, impic, confirmBlocks) => {
  await api.post(`/processes/${pid}/status`, { status: "escriturado", deed_date: deedDate, impic, confirm_blocks: !!confirmBlocks });
  toast.success("Escritura realizada. O processo passou a Concluídos.");
};

export const DeedPrompt = ({ p, onDone, onConfirm, ro, trata }) => {
  const [newDate, setNewDate] = useState("");
  const [postpone, setPostpone] = useState(false);
  const [busy, setBusy] = useState(false);
  if (p.status !== "em_venda") return null;
  if (!p.deed_date) return null;
  if (!p.deed_overdue || ro || trata) return null;
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); onDone(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const savePostpone = () => run(async () => {
    if (!newDate || newDate < todayIso()) throw { response: { data: { detail: "Indica uma nova data futura." } } };
    await api.put(`/processes/${p.id}`, { deed_date: newDate });
    toast.success(`Escritura adiada para ${fmtDate(newDate)}`);
    setPostpone(false); setNewDate("");
  });
  return (
    <div className="rounded-xl px-4 py-3.5 text-sm border bg-blue-50 border-blue-200 text-blue-900 space-y-3" data-testid="deed-overdue-banner">
      <div className="flex items-center gap-2.5 font-medium"><CalendarClock size={16} className="shrink-0" /> A escritura estava marcada para {ddmm(p.deed_date)}. Realizou-se?</div>
      <div className="flex flex-wrap items-center gap-2">
        <button disabled={busy} onClick={() => (onConfirm ? onConfirm() : run(() => markDone(p.id, p.deed_date)))} data-testid="deed-yes-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-ec-green text-white hover:bg-ec-greenDark transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <BadgeCheck size={13} />} Sim
        </button>
        {!postpone ? (
          <button onClick={() => setPostpone(true)} data-testid="deed-postponed-btn" className="px-3 py-1.5 rounded-lg border border-blue-300 hover:bg-blue-100 transition-colors">Foi adiada (nova data)</button>
        ) : (
          <>
            <DateInput min={todayIso()} value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-9 w-44 bg-white" data-testid="deed-new-date" />
            <button disabled={busy} onClick={savePostpone} data-testid="deed-new-date-save" className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-60">Guardar nova data</button>
          </>
        )}
      </div>
    </div>
  );
};

export const EstadoActions = ({ p, onDone, open, setOpen }) => {
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [deedDate, setDeedDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [pays, setPays] = useState([]);
  const [repS, setRepS] = useState({});
  const [repB, setRepB] = useState({});
  useEffect(() => {
    if (open) {
      const d = toInputDate(p.deed_date); setDeedDate(d && d <= todayIso() ? d : todayIso());
      setPays(p.impic?.payments || []); setRepS(p.impic?.rep_seller || {}); setRepB(p.impic?.rep_buyer || {});
    }
  }, [open, p.deed_date, p.impic]);
  if (["escriturado", "sem_venda"].includes(p.status)) return null;
  const impicNeg = (p.mediation?.negocio || "venda") !== "trespasse";
  const blocks = p.todo?.counts?.blocks || [];

  const send = async (status) => {
    if (status === "sem_venda" && !code) { toast.error("Escolhe o motivo."); return; }
    if (status === "sem_venda" && code === "outro" && !reason.trim()) { toast.error("Descreve o motivo."); return; }
    if (status === "escriturado" && (!deedDate || deedDate > todayIso())) { toast.error("Confirma a data em que a escritura se realizou."); return; }
    setBusy(true);
    try {
      if (status === "escriturado") await markDone(p.id, deedDate, impicNeg ? { payments: pays, rep_seller: p.has_procuracao ? repS : null, rep_buyer: p.buyer_procuracao ? repB : null } : undefined, blocks.length > 0);
      else {
        await api.post(`/processes/${p.id}/status`, { status, lost_reason: code, reason: reason.trim() });
        toast.success("Processo arquivado como sem venda");
      }
      setOpen(null); setReason(""); setCode(""); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={!!open} onOpenChange={(o) => !busy && !o && setOpen(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="estado-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-ec-green">{open === "escriturado" ? "Confirmar escritura realizada" : "Encerrar sem venda"}</DialogTitle>
            <DialogDescription>
              {open === "escriturado" ? "Confirma a data. O processo passa a Concluídos." : "Porque é que este imóvel não vai ser vendido por nós? Fica no Histórico e nos números da Direção."}
            </DialogDescription>
          </DialogHeader>
          {open === "escriturado" && blocks.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-900" data-testid="escritura-blocks">
              Há {blocks.length} {blocks.length === 1 ? "bloqueio" : "bloqueios"} por resolver: {blocks.join("; ")}. A escritura realizou-se mesmo assim?
              <div className="text-xs text-red-800/80 mt-1">Ao confirmar, fica registado quem confirmou e quando, e o processo passa ao arquivo.</div>
            </div>
          )}
          {open === "escriturado" && (
            <div><Label>Data da escritura</Label>
              <DateInput max={todayIso()} value={deedDate} onChange={(e) => setDeedDate(e.target.value)} className="mt-1.5" data-testid="escritura-date" />
            </div>
          )}
          {open === "escriturado" && impicNeg && (
            <div className="space-y-3 border-t border-slate-100 pt-3" data-testid="escritura-impic">
              <div>
                <Label>Como foi pago?</Label>
                <p className="text-[13px] text-slate-500">Fica já na Comunicação IMPIC. Vê na escritura: meio, n.º de pagamentos, valor e contas.</p>
              </div>
              <PaymentEditor pays={pays} setPays={setPays} price={p.price} />
              {pays.length === 0 && <p className="text-[13px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="escritura-impic-warning">Sem meio de pagamento, a Comunicação IMPIC fica com dados em falta. Podes preencher depois.</p>}
              {p.has_procuracao && <Rep title="Representante do vendedor (procuração)" rep={repS} onChange={setRepS} tid="escritura-rep-seller" />}
              {p.buyer_procuracao && <Rep title="Representante do comprador (procuração)" rep={repB} onChange={setRepB} tid="escritura-rep-buyer" />}
            </div>
          )}
          {open === "sem_venda" && (
            <div className="space-y-3">
              <div className="grid gap-2" data-testid="lost-reasons">
                {REASONS.filter(([k]) => p.stage !== "angariacao" || ANG_REASONS.has(k)).map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setCode(k)} data-testid={`lost-reason-${k}`} className={`text-left text-[15px] font-medium px-3.5 py-2.5 rounded-lg border transition-colors ${code === k ? "bg-ec-green text-white border-ec-green" : "border-slate-200 hover:bg-slate-50"}`}>{l}</button>
                ))}
              </div>
              {code === "outro" && <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Descreve o motivo" data-testid="sem-venda-reason" />}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => send(open)} disabled={busy} data-testid="estado-confirm"
              className="inline-flex items-center gap-2 bg-ec-green text-white px-4 py-2 rounded-lg hover:bg-ec-greenDark transition-colors disabled:opacity-60">
              {busy && <Loader2 size={14} className="animate-spin" />} {open === "sem_venda" ? "Encerrar" : "Confirmar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
